import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertTriangle, CheckCircle2, ClipboardCheck, Download, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import {
  API_URL, RESULTATS_CONTROLE, STATUT_DOCUMENT, STATUT_VERIFICATION, TYPES_CONTROLE,
  fetchAndDownload, formatDateFr, formatDateTimeFr
} from './constants';

const todayIso = () => new Date().toISOString().slice(0, 10);

const statusIcon = (statut) => {
  if (statut === 'valide') return <CheckCircle2 className="w-4 h-4 text-green-600" />;
  if (statut === 'bientot_expire') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  return <XCircle className="w-4 h-4 text-red-500" />;
};

// ------------------------------------------------------------------ Dialogue nouveau contrôle

const ControleDialog = ({ open, onOpenChange, sousTraitant, hasPrevious, onCreated }) => {
  const [checklist, setChecklist] = useState({ documents: [], types: [], documents_requis: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type_controle: 'periodique', date_controle: todayIso(), resultat: '', commentaire: '' });

  useEffect(() => {
    if (!open) return;
    setForm({ type_controle: hasPrevious ? 'periodique' : 'initial', date_controle: todayIso(), resultat: '', commentaire: '' });
    setLoading(true);
    axios.get(`${API_URL}/sous-traitants/${sousTraitant.id}/documents`, { params: { include_archived: false } })
      .then((res) => setChecklist(res.data))
      .catch((error) => toast.error(getErrorMessage(error, 'Chargement de la checklist impossible')))
      .finally(() => setLoading(false));
  }, [open, sousTraitant.id, hasPrevious]);

  const rows = useMemo(() => {
    const requis = new Set(checklist.documents_requis || []);
    const current = {};
    (checklist.documents || []).forEach((d) => {
      if (d.archive || d.statut_verification === 'rejete') return;
      if (!current[d.type_code] || d.uploaded_at > current[d.type_code].uploaded_at) current[d.type_code] = d;
    });
    const types = (checklist.types || []).filter((t) => t.actif !== false);
    return types
      .map((t) => ({ type: t, requis: requis.has(t.code), doc: current[t.code] || null }))
      .filter((r) => r.requis || r.doc)
      .sort((a, b) => Number(b.requis) - Number(a.requis));
  }, [checklist]);

  const resultatCalcule = useMemo(() => {
    const requisRows = rows.filter((r) => r.requis);
    if (requisRows.some((r) => !r.doc || r.doc.statut === 'expire')) return 'non_conforme';
    if (requisRows.some((r) => r.doc.statut === 'bientot_expire' || r.doc.statut_verification !== 'verifie')) return 'partiel';
    return 'conforme';
  }, [rows]);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/sous-traitants/${sousTraitant.id}/controles`, {
        type_controle: form.type_controle,
        date_controle: form.date_controle,
        resultat: form.resultat || null,
        commentaire: form.commentaire || null
      });
      toast.success('Contrôle enregistré' + (res.data.pv_disponible ? ' — procès-verbal généré' : ''));
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Enregistrement du contrôle impossible'));
    } finally {
      setSaving(false);
    }
  };

  const resultatEffectif = form.resultat || resultatCalcule;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nouveau contrôle de vigilance</DialogTitle>
          <DialogDescription>
            Le contrôle fige un instantané des documents courants et génère un procès-verbal PDF. Il est immuable une fois enregistré.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Type</Label>
              <Select value={form.type_controle} onValueChange={(v) => setForm((f) => ({ ...f, type_controle: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPES_CONTROLE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ctrl-date">Date du contrôle</Label>
              <Input id="ctrl-date" type="date" max={todayIso()} value={form.date_controle} onChange={(e) => setForm((f) => ({ ...f, date_controle: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Résultat</Label>
              <Select value={form.resultat || 'auto'} onValueChange={(v) => setForm((f) => ({ ...f, resultat: v === 'auto' ? '' : v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Automatique ({RESULTATS_CONTROLE[resultatCalcule].label})</SelectItem>
                  {Object.entries(RESULTATS_CONTROLE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border dark:border-slate-700">
            <div className="px-3 py-2 border-b dark:border-slate-700 flex items-center justify-between">
              <span className="text-sm font-semibold flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-[#0F5156]" />Checklist des pièces</span>
              <Badge className={RESULTATS_CONTROLE[resultatEffectif].color}>{RESULTATS_CONTROLE[resultatEffectif].label}</Badge>
            </div>
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#0F5156]" /></div>
            ) : (
              <ul className="divide-y dark:divide-slate-700">
                {rows.map(({ type, requis, doc }) => (
                  <li key={type.code} className="px-3 py-2 flex items-start gap-3 text-sm">
                    {doc ? statusIcon(doc.statut) : <XCircle className="w-4 h-4 text-red-500 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{type.label}</span>
                        {requis && <Badge variant="outline" className="text-[10px]">obligatoire</Badge>}
                        {doc ? (
                          <>
                            <Badge className={STATUT_DOCUMENT[doc.statut]?.color}>{STATUT_DOCUMENT[doc.statut]?.label}</Badge>
                            <Badge className={STATUT_VERIFICATION[doc.statut_verification]?.color}>{STATUT_VERIFICATION[doc.statut_verification]?.label}</Badge>
                          </>
                        ) : <Badge className={STATUT_DOCUMENT.manquant.color}>Manquant</Badge>}
                      </div>
                      {doc && (
                        <p className="text-xs text-slate-500">
                          {doc.fichier_nom} — émis le {formatDateFr(doc.date_emission)}{doc.date_expiration ? `, expire le ${formatDateFr(doc.date_expiration)}` : ''}
                          {doc.numero_reference ? ` — réf. ${doc.numero_reference}` : ''}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
                {rows.length === 0 && <li className="px-3 py-4 text-sm text-slate-500 text-center">Aucun document requis ni déposé.</li>}
              </ul>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="ctrl-comment">Commentaire du contrôleur</Label>
            <Textarea id="ctrl-comment" rows={3} value={form.commentaire} onChange={(e) => setForm((f) => ({ ...f, commentaire: e.target.value }))}
              placeholder="Observations, pièces demandées, réserves…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving || loading} className="bg-[#0F5156] hover:bg-[#0F5156]/90">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-2" />}Valider le contrôle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ------------------------------------------------------------------ Onglet

/**
 * Historique immuable des contrôles de vigilance (timeline) avec téléchargement des PV.
 */
const SousTraitantControlesTab = ({ sousTraitant, canControl, onChanged }) => {
  const [controles, setControles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/sous-traitants/${sousTraitant.id}/controles`);
      setControles(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Chargement des contrôles impossible'));
    } finally {
      setLoading(false);
    }
  }, [sousTraitant.id]);

  useEffect(() => { load(); }, [load]);

  const downloadPv = async (c) => {
    setDownloading(c.id);
    try {
      await fetchAndDownload(`${API_URL}/sous-traitants/${sousTraitant.id}/controles/${c.id}/pdf`, { openInTab: true });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Procès-verbal indisponible'));
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-[#0F5156]" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          {sousTraitant.prochain_controle
            ? <>Prochain contrôle attendu le <span className={`font-semibold ${sousTraitant.controle_en_retard ? 'text-red-600' : ''}`}>{formatDateFr(sousTraitant.prochain_controle)}</span>{sousTraitant.controle_en_retard ? ' (en retard)' : ''}</>
            : <span className="text-red-600 font-medium">Aucun contrôle initial réalisé.</span>}
        </p>
        {canControl && sousTraitant.statut !== 'archive' && (
          <Button size="sm" onClick={() => setDialogOpen(true)} className="bg-[#0F5156] hover:bg-[#0F5156]/90">
            <ClipboardCheck className="w-4 h-4 mr-2" />Nouveau contrôle
          </Button>
        )}
      </div>

      {controles.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-slate-500 dark:border-slate-700">
          Aucun contrôle enregistré. Le premier contrôle doit être réalisé à la signature du contrat.
        </div>
      ) : (
        <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-3 space-y-6">
          {controles.map((c) => {
            const res = RESULTATS_CONTROLE[c.resultat] || RESULTATS_CONTROLE.partiel;
            const manquants = c.documents_manquants || [];
            return (
              <li key={c.id} className="ml-6">
                <span className={`absolute -left-2.5 flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-white dark:ring-slate-900 ${c.resultat === 'conforme' ? 'bg-green-500' : c.resultat === 'partiel' ? 'bg-amber-500' : 'bg-red-500'}`} />
                <div className="rounded-lg border p-4 dark:border-slate-700">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{formatDateFr(c.date_controle)}</span>
                      <Badge variant="outline">{TYPES_CONTROLE[c.type_controle]?.split(' (')[0] || c.type_controle}</Badge>
                      <Badge className={res.color}>{res.label}</Badge>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => downloadPv(c)} disabled={downloading === c.id}>
                      {downloading === c.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Procès-verbal
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Par {c.effectue_par || c.effectue_par_email} — prochaine échéance le {formatDateFr(c.prochaine_echeance)} — enregistré le {formatDateTimeFr(c.created_at)}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(c.documents_verifies || []).map((d) => (
                      <span key={`${c.id}-${d.type_code}`} className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs dark:border-slate-700">
                        {d.statut === 'manquant' ? <XCircle className="w-3 h-3 text-red-500" /> : statusIcon(d.statut)}
                        {d.type_code}
                      </span>
                    ))}
                  </div>
                  {manquants.length > 0 && <p className="text-xs text-red-600 mt-2">Manquants : {manquants.join(', ')}</p>}
                  {c.commentaire && <p className="text-sm mt-2 italic text-slate-600 dark:text-slate-300">« {c.commentaire} »</p>}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <ControleDialog open={dialogOpen} onOpenChange={setDialogOpen} sousTraitant={sousTraitant}
        hasPrevious={controles.length > 0} onCreated={() => { load(); onChanged?.(); }} />
    </div>
  );
};

export default SousTraitantControlesTab;
